import maplibregl, { type ExpressionSpecification } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  initVessels,
  applyVesselFilter,
  setVesselsVisible,
  setVesselOpOverlay,
  getVesselSummary,
  setVesselCountListener,
  defaultVesselFilter,
  VESSEL_ROLES,
  STATE_META,
  OPERATION_META,
  CURRENT_OP_META,
  type VesselFilter,
  type VesselState,
  type VesselOpRole,
  type CurrentOp,
} from "./vessels";
import { initOnshore, setOnshoreVisible, ONSHORE_META } from "./onshore";
import {
  updateOnshoreClusters,
  resetOnshoreClusters,
  updateServiceClusters,
  resetServiceClusters,
  updatePortsClusters,
  resetPortsClusters,
} from "./onshoreClusters";
import { initServicePorts, setServicePortsVisible, SERVICE_META } from "./serviceports";
import { initPorts, setPortsVisible, PORTS_META } from "./ports";
import {
  updateClusterMarkers, resetClusterMarkers, biomassColor, noiseColor, type FarmMode,
} from "./farmClusters";
import {
  renderHarvestChart,
  highlightHarvestMonth,
  harvestPeers,
  getPrice,
  getFxFactor,
  setImpliedFx,
  MONTHS_NO_FULL,
} from "./harvestChart";
import {
  mountAnalytics,
  refreshAnalytics,
  refreshActiveBoard,
  setActiveBoard,
  getActiveBoard,
  type AnalyticsPoint,
} from "./analytics";
import { insightCompanyUrl } from "./insight";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const API_BASE = import.meta.env.VITE_API_BASE;

if (!MAPBOX_TOKEN) {
  throw new Error("VITE_MAPBOX_TOKEN is not set. Copy .env.example to .env and fill it in.");
}

const ZONE_RADIUS_M = 5000;
const NOISE_WINDOW = "7d";
const NOISE_AGGREGATE = "leq";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ZoneProperties = {
  id: string;
  kind: string;
  name: string;
  lon: number;
  lat: number;
  municipality: string | null;
  locality_no: number | null;
  radius_m: number;
};

// Production-cycle state per locality (from /v1/farms/cycle).
type CycleItem = {
  locality_no: number;
  stage: string;
  age_months: number | null;
  cohort_year: number | null;
  cohort_week: number | null;
  capacity_t: number | null;
  po_id: number | null;
  est_biomass_t: number | null;
  harvest_year: number | null;
  harvest_month: number | null;
  harvest_t: number | null;
};
type CycleResponse = { count: number; as_of?: string; items: CycleItem[] };

// Per-farm CURRENT operation, from /v1/farms/operations — the single source of
// truth. `source` is "live" when a vessel is sitting at the site right now (the
// vessel's op; a service/feed boat → "service"), else "recent" (the backend's
// recent-window classification). The map just renders this.
// "delousing_due" is a distinct class: lice over the model trigger → delousing
// is needed (forward-looking planning), NOT a past op. It carries `reason` and
// `last_done` instead of an `event_date`.
type FarmOp = "delousing" | "delousing_due" | "harvest" | "smolt" | "service";
type FarmOpItem = {
  locality_no: number;
  op: FarmOp;
  source: "live" | "recent";
  state: "active" | "recent" | "due" | "window";
  vessel: string | null;
  vessel_role: string | null;
  event_date: string | null;
  last_done: string | null;
  reason: string | null;
  n_vessels: number | null;
  method: string | null;
};
type FarmOpsResponse = { count: number; items: FarmOpItem[] };

// Resolved per-site op state kept for enrich() + the popup.
type FarmOpInfo = {
  op: FarmOp;
  live: boolean;
  state: FarmOpItem["state"];
  vessel: string | null;
  eventDate: string | null;
  lastDone: string | null;
  reason: string | null;
  nVessels: number | null;
  method: string | null;
};

type SalmonResponse = {
  unit: string;
  anchor: number | null;
  as_of?: string;
  forecast: { year: number; month: number; price_nok_kg: number }[];
};

type ZonesResponse = {
  type: "FeatureCollection";
  count: number;
  features: GeoJSON.Feature<GeoJSON.Polygon, ZoneProperties>[];
};

type HeatmapItem = {
  receiver_id: string;
  name: string;
  lon: number;
  lat: number;
  db: number;
  n: number;
};

type HeatmapResponse = {
  from: string;
  to: string;
  model_version_id: string;
  aggregate: string;
  items: HeatmapItem[];
};

type ReceiverStatus = {
  receiver_id: string;
  locality_no: number | null;
  year: number | null;
  week: number | null;
  is_fallow: boolean;
  has_lice: boolean;
  has_treat: boolean;
  has_pd: boolean;
  has_ila: boolean;
  avg_lice: number | null;
  operator_slugs: string[];
  operator_names: string[];
  species: string | null;
  species_groups: SpeciesGroup[];
};

type StatusResponse = { count: number; items: ReceiverStatus[] };

// Farmed-type buckets, mirrored from the API's _species_groups. Used both as
// the per-feature classification and as the filter checkbox set.
type SpeciesGroup = "laksefisk" | "torsk" | "annen_fisk" | "skjell" | "tare" | "annet";
const SPECIES_GROUPS: readonly SpeciesGroup[] = [
  "laksefisk", "torsk", "annen_fisk", "skjell", "tare", "annet",
] as const;
const SPECIES_GROUP_LABELS: Record<SpeciesGroup, string> = {
  laksefisk: "Laksefisk",
  torsk: "Torsk",
  annen_fisk: "Annen fisk",
  skjell: "Skjell",
  tare: "Tare og alger",
  annet: "Annet",
};

type OperatorListItem = {
  id: string;
  slug: string;
  name: string;
  n_sites: number;
};

type OperatorsResponse = { count: number; items: OperatorListItem[] };

type Tier = "Stille" | "Moderat" | "Forhøyet" | "Høy" | "Kritisk" | "Ingen data";
const TIERS: readonly Tier[] = ["Stille", "Moderat", "Forhøyet", "Høy", "Kritisk"] as const;

type Thresholds = { p25: number; p50: number; p75: number; p95: number };

type EnrichedProperties = ZoneProperties & {
  db: number | null;
  tier: Tier;
  is_fallow: boolean;
  has_lice: boolean;
  has_treat: boolean;
  has_pd: boolean;
  has_ila: boolean;
  operator_slugs: string[];
  operator_names: string[];
  avg_lice: number | null;
  health_year: number | null;
  health_week: number | null;
  species: string | null;
  species_groups: SpeciesGroup[];
  // Production-cycle (from /v1/farms/cycle), merged by locality_no.
  est_biomass_t: number | null;
  fish_stage: string | null;
  age_months: number | null;
  capacity_t: number | null;
  cohort_year: number | null;
  cohort_week: number | null;
  harvest_year: number | null;
  harvest_month: number | null;
  harvest_t: number | null;
  // Current operation at the site. From live vessel presence when a vessel is
  // at the site (op_is_live=true, op_vessel set), else the backend recent-window
  // classification (/v1/farms/operations).
  farm_op: FarmOp | null;
  op_is_live: boolean;
  op_vessel: string | null;
  // Set client-side once compliance audits load: does this site's operator have
  // an avvik? Drives the compliance cluster aggregate + highlight.
  has_avvik?: boolean;
};

// Cycle state keyed by locality_no, populated in render(); enrich() reads it.
const cycleByLoc = new Map<number, CycleItem>();
// Per-farm operation keyed by locality_no (op + live flag + vessel), straight
// from /v1/farms/operations; enrich() reads it.
const farmOpByLoc = new Map<number, FarmOpInfo>();

// ---------------------------------------------------------------------------
// dB color ramp · used by both layer paint and the legend
// ---------------------------------------------------------------------------

// Calibrated to the 7-day Leq distribution across the population
// (p5≈119, p25≈126, p50≈131, p75≈139, p95≈146 dB). 10-dB steps from 115
// to 155 cover the full active range without saturating at either end.
const DB_RAMP: ExpressionSpecification = [
  "interpolate",
  ["linear"],
  ["coalesce", ["get", "db"], 0],
  115, "#2c7bb6",
  125, "#abd9e9",
  135, "#ffffbf",
  145, "#fdae61",
  155, "#d7191c",
];

const NO_DATA_COLOR = "#5a6570";

const colorByDb: ExpressionSpecification = [
  "case",
  ["==", ["typeof", ["get", "db"]], "number"],
  DB_RAMP,
  NO_DATA_COLOR,
];

// Biomass ramp (estimated tonnes/farm) · teal → green → gold → coral. Matches
// the JS ramp in farmClusters.ts so individual farms and cluster bubbles agree.
const BIOMASS_RAMP: ExpressionSpecification = [
  "interpolate", ["linear"], ["max", 0, ["coalesce", ["get", "est_biomass_t"], 0]],
  0, "#2a9d8f",
  800, "#76b17d",
  1800, "#e9c46a",
  3200, "#f4a261",
  5000, "#e76f51",
];
// Farms with no live estimate (fallow / land-based / continuous) read grey.
const colorByBiomass: ExpressionSpecification = [
  "case",
  ["<", ["coalesce", ["get", "est_biomass_t"], -1], 0], NO_DATA_COLOR,
  BIOMASS_RAMP,
];
const radiusByBiomass: ExpressionSpecification = [
  "interpolate", ["linear"], ["zoom"],
  9, ["+", 2.5, ["*", 0.09, ["sqrt", ["max", 0, ["coalesce", ["get", "est_biomass_t"], 0]]]]],
  14, ["+", 4, ["*", 0.18, ["sqrt", ["max", 0, ["coalesce", ["get", "est_biomass_t"], 0]]]]],
];
const radiusByDb: ExpressionSpecification = [
  "interpolate", ["linear"], ["zoom"], 3, 2.5, 8, 5, 12, 9,
];

// Current per-farm operation (farm_op) → ring colour. Same palette as the
// vessel current_op halo (CURRENT_OP_META), shared via FARM_OP_META below.
const FARM_OP_META: Record<FarmOp, { label: string; color: string }> = {
  delousing: { label: "Avlusing", color: "#F4A261" },
  delousing_due: { label: "Avlusing forfaller", color: "#E76F51" },
  harvest: { label: "Høsting", color: "#C77DFF" },
  smolt: { label: "Smolt", color: "#7BE3C6" },
  service: { label: "Service / fôr", color: "#8AA99A" },
};
const FARM_OP_KEYS = Object.keys(FARM_OP_META) as FarmOp[];
const colorByFarmOp: ExpressionSpecification = [
  "match", ["get", "farm_op"],
  "delousing", FARM_OP_META.delousing.color,
  "delousing_due", FARM_OP_META.delousing_due.color,
  "harvest", FARM_OP_META.harvest.color,
  "smolt", FARM_OP_META.smolt.color,
  "service", FARM_OP_META.service.color,
  "#000000",
];
// Sit the op ring just outside the farm dot in either metric mode.
const farmOpRingRadius: ExpressionSpecification = [
  "+", 3, ["max", radiusByBiomass, radiusByDb],
];
// Highlight ring (lice / compliance lenses) sits a touch further out.
const farmHighlightRadius: ExpressionSpecification = [
  "+", 5, ["max", radiusByBiomass, radiusByDb],
];

const LICE_THRESHOLD = 0.5; // adult female lice · regulatory action limit

// Canvas glyph for a farm's current operation — a coloured disc with a white
// pictogram, registered as a map image (the style carries no glyph font, so we
// rasterise icons the same way vessels.ts does).
// Logical (CSS) icon size and the device-pixel ratio we rasterise at. We render
// the bitmap at LOGICAL × PR physical pixels and register the image at
// pixelRatio PR, so MapLibre treats it as a LOGICAL-px icon with PR× the texels
// — crisp on retina, no upscaling at icon-size 1.
const FARM_OP_LOGICAL = 34;
const FARM_OP_PR = Math.max(4, Math.ceil((window.devicePixelRatio || 1) * 2));

function farmOpGlyph(op: FarmOp): ImageData {
  const L = FARM_OP_LOGICAL;
  const PR = FARM_OP_PR;
  const c = document.createElement("canvas");
  c.width = L * PR;
  c.height = L * PR;
  const ctx = c.getContext("2d")!;
  ctx.scale(PR, PR);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  const cx = L / 2;
  const cy = L / 2;
  const r = L / 2 - 4;
  // Disc + white rim.
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.45)";
  ctx.shadowBlur = 3.5;
  ctx.shadowOffsetY = 1.2;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = FARM_OP_META[op].color;
  ctx.fill();
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(255,255,255,0.96)";
  ctx.stroke();
  ctx.fillStyle = "#fff";
  if (op === "delousing") {
    // Droplet (treatment).
    ctx.beginPath();
    ctx.moveTo(cx, cy - 7.5);
    ctx.bezierCurveTo(cx + 6.5, cy + 0.5, cx + 4.8, cy + 7.5, cx, cy + 7.5);
    ctx.bezierCurveTo(cx - 4.8, cy + 7.5, cx - 6.5, cy + 0.5, cx, cy - 7.5);
    ctx.closePath();
    ctx.fill();
  } else if (op === "harvest") {
    // Down arrow (taking fish out).
    ctx.beginPath();
    ctx.moveTo(cx, cy + 7.5);
    ctx.lineTo(cx - 6, cy - 0.5);
    ctx.lineTo(cx - 2.4, cy - 0.5);
    ctx.lineTo(cx - 2.4, cy - 7.5);
    ctx.lineTo(cx + 2.4, cy - 7.5);
    ctx.lineTo(cx + 2.4, cy - 0.5);
    ctx.lineTo(cx + 6, cy - 0.5);
    ctx.closePath();
    ctx.fill();
  } else if (op === "smolt") {
    // Small fish (smolt in).
    ctx.beginPath();
    ctx.ellipse(cx - 1, cy, 6.6, 4.2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(cx + 4.8, cy);
    ctx.lineTo(cx + 8.8, cy - 4);
    ctx.lineTo(cx + 8.8, cy + 4);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = FARM_OP_META[op].color;
    ctx.beginPath();
    ctx.arc(cx - 4.4, cy - 0.6, 1.1, 0, Math.PI * 2);
    ctx.fill();
  } else if (op === "delousing_due") {
    // Sea louse — lice over the limit, delousing due. Body + carapace + legs.
    ctx.save();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.ellipse(cx, cy - 0.3, 4.4, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = FARM_OP_META[op].color;
    ctx.beginPath();
    ctx.ellipse(cx, cy - 3, 3, 2.3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff";
    for (let i = 0; i < 3; i++) {
      const yy = cy - 1 + i * 2.9;
      ctx.beginPath();
      ctx.moveTo(cx - 3.4, yy);
      ctx.lineTo(cx - 7, yy + (i - 1) * 1.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx + 3.4, yy);
      ctx.lineTo(cx + 7, yy + (i - 1) * 1.5);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(cx - 1.5, cy + 5.4);
    ctx.lineTo(cx - 2.6, cy + 8.4);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 1.5, cy + 5.4);
    ctx.lineTo(cx + 2.6, cy + 8.4);
    ctx.stroke();
    ctx.restore();
  } else {
    // Service / feed — a simple gear (maintenance / tending the site).
    const teeth = 8;
    const rOuter = 7.4;
    const rInner = 5.4;
    ctx.beginPath();
    for (let i = 0; i < teeth * 2; i++) {
      const rr = i % 2 === 0 ? rOuter : rInner;
      const a = (Math.PI / teeth) * i - Math.PI / 2;
      const x = cx + Math.cos(a) * rr;
      const y = cy + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = FARM_OP_META[op].color;
    ctx.beginPath();
    ctx.arc(cx, cy, 2.4, 0, Math.PI * 2);
    ctx.fill();
  }
  return ctx.getImageData(0, 0, L * PR, L * PR);
}

// Minimal shape of /v1/compliance/audits for the compliance map lens.
type AuditLite = { n_avvik: number; operator_slug: string | null };
type AuditsResp = { items: AuditLite[] };
function slugsFromAudits(r: AuditsResp): Set<string> {
  const s = new Set<string>();
  for (const a of r.items) {
    if (a.operator_slug && (a.n_avvik ?? 0) > 0) s.add(a.operator_slug);
  }
  return s;
}
let auditSlugs: Set<string> | null = null;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, init);
  if (!res.ok) {
    throw new Error(`${path} → ${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Stale-while-revalidate cache · the heatmap query takes ~37s server-side, so
// repeat visits should paint colours from cache and update silently.
// ---------------------------------------------------------------------------

const CACHE_PREFIX = "phoniq-map.v1.";
const CACHE_TTL_MS = 60 * 60 * 1000;

function readCache<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return undefined;
    const o = JSON.parse(raw) as { t: number; v: T };
    if (!o || typeof o.t !== "number") return undefined;
    if (Date.now() - o.t > CACHE_TTL_MS) return undefined;
    return o.v;
  } catch {
    return undefined;
  }
}

function writeCache<T>(key: string, value: T): void {
  const payload = JSON.stringify({ t: Date.now(), v: value });
  try {
    localStorage.setItem(CACHE_PREFIX + key, payload);
  } catch {
    // Quota exceeded — clear our keys and retry once.
    try {
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith(CACHE_PREFIX)) localStorage.removeItem(k);
      }
      localStorage.setItem(CACHE_PREFIX + key, payload);
    } catch {
      /* give up silently */
    }
  }
}

function setLoadingText(text: string): void {
  const t = document.getElementById("loading-text");
  if (t) t.textContent = text;
}

function hideLoading(): void {
  const el = document.getElementById("loading");
  if (el) el.classList.add("is-hidden");
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Quartile thresholds across the population. Tier classification is rank-based
// (matches the report's percentile model), not absolute dB cutoffs.
function computeThresholds(values: number[]): Thresholds {
  if (values.length === 0) return { p25: NaN, p50: NaN, p75: NaN, p95: NaN };
  const sorted = [...values].sort((a, b) => a - b);
  const at = (p: number): number => {
    const i = Math.max(0, Math.min(sorted.length - 1, Math.floor(p * (sorted.length - 1))));
    return sorted[i] ?? NaN;
  };
  return { p25: at(0.25), p50: at(0.5), p75: at(0.75), p95: at(0.95) };
}

function tierFor(db: number | null, t: Thresholds): Tier {
  if (db == null) return "Ingen data";
  if (db <= t.p25) return "Stille";
  if (db <= t.p50) return "Moderat";
  if (db <= t.p75) return "Forhøyet";
  if (db <= t.p95) return "Høy";
  return "Kritisk";
}

// "MOWI SEAWATER NORWAY AS" → "Mowi Seawater Norway AS"
function titleCaseOperator(name: string): string {
  const SHORT = new Set(["AS", "ASA", "DA", "ANS", "I", "II", "III"]);
  return name
    .split(/\s+/)
    .map((w) => (SHORT.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(" ");
}

// MapLibre serialises array properties to JSON strings when reading back from
// features. Reverse that on the way out so the popup can use real arrays.
function readArrayProp(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw as string[];
  if (typeof raw === "string" && raw.startsWith("[")) {
    try {
      const v = JSON.parse(raw);
      return Array.isArray(v) ? (v as string[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// GeoJSON enrichment
// ---------------------------------------------------------------------------

function enrich(
  base: ZoneProperties,
  statusByReceiver: Map<string, ReceiverStatus>,
  dbByReceiver: Map<string, number>,
  thresholds: Thresholds,
): EnrichedProperties {
  const db = dbByReceiver.get(base.id) ?? null;
  const status = statusByReceiver.get(base.id);
  const cyc = base.locality_no != null ? cycleByLoc.get(base.locality_no) : undefined;
  return {
    ...base,
    db,
    tier: tierFor(db, thresholds),
    is_fallow: status?.is_fallow ?? false,
    has_lice: status?.has_lice ?? false,
    has_treat: status?.has_treat ?? false,
    has_pd: status?.has_pd ?? false,
    has_ila: status?.has_ila ?? false,
    operator_slugs: status?.operator_slugs ?? [],
    operator_names: status?.operator_names ?? [],
    avg_lice: status?.avg_lice ?? null,
    health_year: status?.year ?? null,
    health_week: status?.week ?? null,
    species: status?.species ?? null,
    species_groups: status?.species_groups ?? [],
    est_biomass_t: cyc?.est_biomass_t ?? null,
    fish_stage: cyc?.stage ?? null,
    age_months: cyc?.age_months ?? null,
    capacity_t: cyc?.capacity_t ?? null,
    cohort_year: cyc?.cohort_year ?? null,
    cohort_week: cyc?.cohort_week ?? null,
    harvest_year: cyc?.harvest_year ?? null,
    harvest_month: cyc?.harvest_month ?? null,
    harvest_t: cyc?.harvest_t ?? null,
    ...farmOpFor(base.locality_no),
  };
}

// A site's operation as resolved by the backend (live vessel presence or the
// recent-window fallback). The map only renders; it does not infer.
function farmOpFor(loc: number | null): { farm_op: FarmOp | null; op_is_live: boolean; op_vessel: string | null } {
  const info = loc != null ? farmOpByLoc.get(loc) : undefined;
  if (!info) return { farm_op: null, op_is_live: false, op_vessel: null };
  return { farm_op: info.op, op_is_live: info.live, op_vessel: info.vessel };
}

function buildEnrichedPoints(
  zones: ZonesResponse,
  statusByReceiver: Map<string, ReceiverStatus>,
  dbByReceiver: Map<string, number>,
  thresholds: Thresholds,
): GeoJSON.FeatureCollection<GeoJSON.Point, EnrichedProperties> {
  return {
    type: "FeatureCollection",
    features: zones.features.map((f) => ({
      type: "Feature",
      id: f.id,
      geometry: { type: "Point", coordinates: [f.properties.lon, f.properties.lat] },
      properties: enrich(f.properties, statusByReceiver, dbByReceiver, thresholds),
    })),
  };
}

function buildEnrichedZones(
  zones: ZonesResponse,
  statusByReceiver: Map<string, ReceiverStatus>,
  dbByReceiver: Map<string, number>,
  thresholds: Thresholds,
): GeoJSON.FeatureCollection<GeoJSON.Polygon, EnrichedProperties> {
  return {
    type: "FeatureCollection",
    features: zones.features.map((f) => ({
      ...f,
      properties: enrich(f.properties, statusByReceiver, dbByReceiver, thresholds),
    })),
  };
}

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

type FilterState = {
  status: "all" | "active" | "fallow";
  tiers: Set<Tier>;
  speciesGroups: Set<SpeciesGroup>;
  operatorSlug: string;
  hasLiceOnly: boolean;
  hasViralOnly: boolean;
};

// "Ingen data" is in the default set so no-data receivers stay visible during
// the initial paint (before heatmap lands every feature has db=null).
const defaultTiers = (): Set<Tier> => new Set<Tier>([...TIERS, "Ingen data"]);
const defaultSpeciesGroups = (): Set<SpeciesGroup> => new Set<SpeciesGroup>(SPECIES_GROUPS);

const filterState: FilterState = {
  status: "all",
  tiers: defaultTiers(),
  speciesGroups: defaultSpeciesGroups(),
  operatorSlug: "",
  hasLiceOnly: false,
  hasViralOnly: false,
};

// Persist the farm filters across reloads (localStorage) so the map reopens with
// the same selection. Camera + active board are remembered separately.
const FILTERS_KEY = "phoniq-map.filters";
type SavedFilters = {
  status: FilterState["status"];
  tiers: Tier[];
  speciesGroups: SpeciesGroup[];
  operatorSlug: string;
  hasLiceOnly: boolean;
  hasViralOnly: boolean;
};

function saveFilters(): void {
  try {
    const f: SavedFilters = {
      status: filterState.status,
      tiers: [...filterState.tiers],
      speciesGroups: [...filterState.speciesGroups],
      operatorSlug: filterState.operatorSlug,
      hasLiceOnly: filterState.hasLiceOnly,
      hasViralOnly: filterState.hasViralOnly,
    };
    localStorage.setItem(FILTERS_KEY, JSON.stringify(f));
  } catch {
    /* ignore quota */
  }
}

function loadFilters(): void {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return;
    const f = JSON.parse(raw) as Partial<SavedFilters>;
    if (f.status === "all" || f.status === "active" || f.status === "fallow") {
      filterState.status = f.status;
    }
    if (Array.isArray(f.tiers)) {
      const valid = new Set<string>([...TIERS, "Ingen data"]);
      filterState.tiers = new Set(f.tiers.filter((t): t is Tier => valid.has(t)));
    }
    if (Array.isArray(f.speciesGroups)) {
      const valid = new Set<string>(SPECIES_GROUPS);
      filterState.speciesGroups = new Set(
        f.speciesGroups.filter((g): g is SpeciesGroup => valid.has(g)),
      );
    }
    if (typeof f.operatorSlug === "string") filterState.operatorSlug = f.operatorSlug;
    if (typeof f.hasLiceOnly === "boolean") filterState.hasLiceOnly = f.hasLiceOnly;
    if (typeof f.hasViralOnly === "boolean") filterState.hasViralOnly = f.hasViralOnly;
  } catch {
    /* ignore */
  }
}

loadFilters();

// The Aktive/Brakklagte status comes from BarentsWatch's salmonid lice/health
// regime (isFallow), which is meaningless for cod/shellfish/kelp — those read as
// permanently fallow. So the status filter (and the "brakklagt" label) apply
// only to farms that actually carry salmonids; everything else is exempt.
function isSalmonidFarm(p: EnrichedProperties): boolean {
  return p.species_groups.includes("laksefisk");
}

function passesFilters(p: EnrichedProperties): boolean {
  if (isSalmonidFarm(p)) {
    if (filterState.status === "active" && p.is_fallow) return false;
    if (filterState.status === "fallow" && !p.is_fallow) return false;
  }
  if (!filterState.tiers.has(p.tier)) return false;
  // Species: only filters when not all groups are selected. A feature passes
  // if any of its farmed-type groups is checked; species-less features carry
  // ["annet"] from the API so they follow the "Annet" checkbox.
  if (filterState.speciesGroups.size < SPECIES_GROUPS.length) {
    const groups = p.species_groups.length ? p.species_groups : (["annet"] as SpeciesGroup[]);
    if (!groups.some((g) => filterState.speciesGroups.has(g))) return false;
  }
  if (filterState.operatorSlug && !p.operator_slugs.includes(filterState.operatorSlug)) return false;
  if (filterState.hasLiceOnly && !p.has_lice) return false;
  if (filterState.hasViralOnly && !(p.has_pd || p.has_ila)) return false;
  return true;
}

function filterFC<G extends GeoJSON.Geometry>(
  fc: GeoJSON.FeatureCollection<G, EnrichedProperties>,
): GeoJSON.FeatureCollection<G, EnrichedProperties> {
  return {
    type: "FeatureCollection",
    features: fc.features.filter((f) => passesFilters(f.properties)),
  };
}

// ---------------------------------------------------------------------------
// Popup
// ---------------------------------------------------------------------------

// What the live-operation glyph means, phrased as an action.
const FARM_OP_VERB: Record<FarmOp, string> = {
  delousing: "Avlusing pågår",
  delousing_due: "Avlusing forfaller",
  harvest: "Høsting pågår",
  smolt: "Smolt satt ut",
  service: "Service / fôring pågår",
};

// "27. apr 2026 · for 8 uker siden" — an absolute date plus an intuitive
// relative phrase, so a recent op reads as a real moment, not a vague "nylig".
function opWhenText(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "nylig";
  const abs = d.toLocaleDateString("no", { day: "numeric", month: "short", year: "numeric" });
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  let rel: string;
  if (days <= 0) rel = "i dag";
  else if (days === 1) rel = "i går";
  else if (days < 14) rel = `for ${days} dager siden`;
  else if (days < 60) rel = `for ${Math.round(days / 7)} uker siden`;
  else rel = `for ${Math.round(days / 30)} mnd siden`;
  return `${abs} · ${rel}`;
}

function shortDateNo(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("no", { day: "numeric", month: "short", year: "numeric" });
}

// The delousing-due reason is phrased in English by the model; soften to Norwegian.
function reasonNo(s: string): string {
  return s
    .replace(/lice/gi, "lus")
    .replace(/model trigger/gi, "grense")
    .replace(/approaching/gi, "nærmer seg")
    .replace(/trigger/gi, "grense");
}

// Treatment-method strings come from the ops log in English; soften to Norwegian.
const METHOD_NO: Record<string, string> = {
  mechanical: "mekanisk",
  "mechanical / freshwater bath (long)": "mekanisk / ferskvann",
  "freshwater bath (long)": "ferskvann",
  freshwater: "ferskvann",
  thermal: "termisk",
};
function methodLabel(m: string): string {
  return METHOD_NO[m] ?? m;
}

// A small inline copy of the map glyph (same canvas as farm-op-icon), so the
// popup shows the user the exact symbol they see on the map.
function glyphChip(op: FarmOp): HTMLElement {
  const wrap = document.createElement("span");
  wrap.className = "pl-glyph";
  const img = farmOpGlyph(op);
  const cv = document.createElement("canvas");
  cv.width = img.width;
  cv.height = img.height;
  cv.style.width = "18px";
  cv.style.height = "18px";
  cv.getContext("2d")!.putImageData(img, 0, 0);
  wrap.appendChild(cv);
  return wrap;
}

// The on-site key: explains the dot colour (per the active map mode) and the
// live-operation glyph if one is drawn on this site.
function popupLegend(p: EnrichedProperties): HTMLElement {
  const box = document.createElement("div");
  box.className = "popup-legend";

  // Row 1 — what the farm dot's colour encodes right now.
  const colorRow = document.createElement("div");
  colorRow.className = "pl-row";
  const sw = document.createElement("span");
  sw.className = "pl-swatch";
  let colorText: string;
  if (farmMode === "noise") {
    sw.style.background = p.db == null ? NO_DATA_COLOR : noiseColor(p.db);
    colorText = p.db == null
      ? "Farge = støynivå · ingen data"
      : `Farge = støynivå · ${p.db.toFixed(0)} dB`;
  } else {
    const t = p.est_biomass_t;
    sw.style.background = t == null ? NO_DATA_COLOR : biomassColor(t);
    colorText = t == null
      ? "Farge = biomasse · ingen anslag"
      : `Farge = biomasse · ~${t.toLocaleString("no")} t`;
  }
  colorRow.appendChild(sw);
  const ct = document.createElement("span");
  ct.textContent = colorText;
  colorRow.appendChild(ct);
  box.appendChild(colorRow);

  // Row 2 — the operation glyph. A live vessel-present op is phrased as "pågår"
  // and names the vessel; the backend recent-window op is marked "nylig (AIS)"
  // so a service boat sitting at a "harvest"-tagged site no longer reads as an
  // active harvest.
  if (p.farm_op && FARM_OP_KEYS.includes(p.farm_op)) {
    const info = p.locality_no != null ? farmOpByLoc.get(p.locality_no) : undefined;
    const opRow = document.createElement("div");
    opRow.className = "pl-row";
    opRow.appendChild(glyphChip(p.farm_op));
    const ot = document.createElement("span");
    const subRows: string[] = []; // extra context lines (innerHTML)

    if (p.op_is_live) {
      ot.textContent = p.op_vessel
        ? `${FARM_OP_VERB[p.farm_op]} · ${titleCaseOperator(p.op_vessel)}`
        : FARM_OP_VERB[p.farm_op];
    } else if (p.farm_op === "delousing_due") {
      // A forecast, not a past event: lice over the trigger → delousing needed.
      ot.innerHTML = `${FARM_OP_META.delousing_due.label} <span class="pl-due">forfaller</span>`;
      if (info?.reason) subRows.push(reasonNo(info.reason));
      if (info?.lastDone) subRows.push(`Sist avluset ${opWhenText(info.lastDone)}`);
    } else if (info?.state === "window") {
      ot.innerHTML =
        `${FARM_OP_META[p.farm_op].label} · ` +
        `<span class="pl-recent">estimert vindu${info.eventDate ? " fra " + shortDateNo(info.eventDate) : ""}</span>`;
    } else {
      // Recent op: say *when* it happened instead of a dateless "nylig".
      ot.innerHTML =
        `${FARM_OP_META[p.farm_op].label} · ` +
        `<span class="pl-recent">${info?.eventDate ? opWhenText(info.eventDate) : "nylig"}</span>`;
      if (info?.vessel) {
        const extra = info.nVessels && info.nVessels > 1 ? ` +${info.nVessels - 1} fartøy` : "";
        const meth = info.method ? ` · ${methodLabel(info.method)}` : "";
        subRows.push(`Utført av <b>${titleCaseOperator(info.vessel)}</b>${extra}${meth}`);
      }
    }
    opRow.appendChild(ot);
    box.appendChild(opRow);

    for (const sr of subRows) {
      const sub = document.createElement("div");
      sub.className = "pl-row pl-subrow";
      sub.innerHTML = `<span class="pl-bullet">↳</span><span>${sr}</span>`;
      box.appendChild(sub);
    }
  }

  return box;
}

function popupContent(p: EnrichedProperties): HTMLElement {
  const root = document.createElement("div");
  root.className = "popup";

  const h3 = document.createElement("h3");
  h3.textContent = p.name;
  root.appendChild(h3);

  if (p.municipality) {
    const muni = document.createElement("div");
    muni.className = "muted";
    muni.textContent = p.municipality;
    root.appendChild(muni);
  }

  // Tier badge + dB on one line
  const head = document.createElement("div");
  head.className = "popup-head";
  const badge = document.createElement("span");
  badge.className = `popup-tier popup-tier-${p.tier.toLowerCase().replace(/\s+/g, "-")}`;
  badge.textContent = p.tier.toUpperCase();
  head.appendChild(badge);
  const dbText = document.createElement("span");
  dbText.className = "db";
  dbText.textContent = p.db == null ? "ingen data" : `${p.db.toFixed(1)} dB`;
  head.appendChild(dbText);
  root.appendChild(head);

  // "How to read this site" — explain the dot colour (in the active mode) and,
  // if the site has a live operation, the exact glyph drawn on the map. This is
  // the on-site key: the map's symbols are otherwise explained nowhere.
  root.appendChild(popupLegend(p));

  // Operator
  if (p.operator_names.length > 0) {
    root.appendChild(makePopupRow("Eier", p.operator_names.map(titleCaseOperator).join(", ")));
    // Deep-link the first operator with a slug into Insight's company story.
    const slug = p.operator_slugs.find(Boolean);
    if (slug) {
      const link = document.createElement("a");
      link.className = "popup-insight";
      link.href = insightCompanyUrl(slug);
      link.target = "_blank";
      link.rel = "noopener";
      link.textContent = "Åpne i Insight →";
      root.appendChild(link);
    }
    // Mattilsynet avvik for this operator — shown for flagged sites (or while the
    // compliance board is active), lazily fetched on open.
    if (slug && (p.has_avvik || getActiveBoard() === "compliance")) {
      const audit = document.createElement("div");
      audit.className = "popup-audit";
      audit.innerHTML =
        `<div class="popup-label">Avvik · Mattilsynet</div>` +
        `<div class="audit-body muted">laster …</div>`;
      root.appendChild(audit);
      void loadAuditFindings(slug, audit.querySelector<HTMLElement>(".audit-body")!);
    }
  }

  // Farmed species (raw register string)
  if (p.species) {
    root.appendChild(makePopupRow("Art", p.species));
  }

  // Latest fishhealth. The brakklagt/lice/PD/ILA regime is salmonid-only, so
  // skip the "brakklagt" label on non-salmonid farms (they always read fallow).
  if (p.health_year != null && p.health_week != null) {
    const bits: string[] = [];
    if (p.is_fallow && isSalmonidFarm(p)) bits.push("brakklagt");
    if (p.has_lice) {
      bits.push(p.avg_lice != null
        ? `lus rapportert (${p.avg_lice.toFixed(2)} hf/fisk)`
        : "lus rapportert");
    }
    if (p.has_pd)  bits.push("PD rapportert");
    if (p.has_ila) bits.push("ILA rapportert");
    if (p.has_treat) bits.push("behandling");
    if (bits.length === 0) bits.push("ingen hendelser");
    root.appendChild(
      makePopupRow(`Helse uke ${p.health_week}/${p.health_year}`, bits.join(", ")),
    );
  }

  // Production-cycle: estimated biomass + fish age (from /v1/farms/cycle).
  const STAGE_LABEL: Record<string, string> = {
    ung: "ung (0–6 mnd)", vekst: "vekst (6–12 mnd)", moden: "moden (12–18 mnd)",
    slakteklar: "slakteklar (18 mnd+)", brakk: "brakklagt / tom", ukjent: "ukjent",
    annen_art: "oppdrett (annen art)",
  };
  if (p.est_biomass_t != null || p.fish_stage) {
    if (p.est_biomass_t != null) {
      root.appendChild(makePopupRow("Biomasse", `~${p.est_biomass_t.toLocaleString("no")} t (anslag)`));
    }
    if (p.fish_stage) {
      const age = p.age_months != null ? ` · ${p.age_months} mnd i sjø` : "";
      root.appendChild(makePopupRow("Fisk", `${STAGE_LABEL[p.fish_stage] ?? p.fish_stage}${age}`));
    }
    // Estimated harvest month + where it ranks among all sites.
    if (p.harvest_year != null && p.harvest_month != null) {
      const peers = harvestPeers([...cycleByLoc.values()], p.harvest_year, p.harvest_month);
      const when = `${MONTHS_NO_FULL[p.harvest_month - 1] ?? ""} ${p.harvest_year}`;
      root.appendChild(makePopupRow(
        "Slakt (anslag)",
        `${when} · ${peers.beforePct}% av anlegg slaktes før · ${peers.same} samtidig`,
      ));
      // Estimated harvest value = projected tonnage × forecast price × FX.
      const price = getPrice(p.harvest_year, p.harvest_month);
      if (price != null && p.harvest_t != null) {
        const mnok = (p.harvest_t * price * getFxFactor()) / 1000;
        root.appendChild(makePopupRow(
          "Verdi (anslag)",
          `~${mnok.toLocaleString("no", { maximumFractionDigits: 0 })} MNOK ` +
          `(${p.harvest_t.toLocaleString("no")} t × ${price.toFixed(0)} kr/kg)`,
        ));
        const timing = buildTimingTool(p);
        if (timing) root.appendChild(timing);
      }
    }
  }

  // Vessel-activity summary (what was active near the farm this week).
  const act = document.createElement("div");
  act.className = "popup-activity";
  act.innerHTML =
    `<div class="popup-label">Fartøyaktivitet (7 d)</div>` +
    `<div class="pa-body muted">laster …</div>`;
  root.appendChild(act);
  void loadVesselActivity(p.id, act.querySelector(".pa-body") as HTMLElement);

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "popup-action";
  btn.textContent = "Last ned ukerapport (PDF)";
  btn.addEventListener("click", () => downloadWeeklyReport(p.id, btn));
  root.appendChild(btn);

  return root;
}

type VesselActivity = {
  n_vessels: number;
  by_role: Record<string, number>;
  by_op: Record<string, number>;
  vessels: { name: string | null; role: string; op: string | null; op_label: string | null }[];
};

const ROLE_LABEL_NO: Record<string, string> = {
  wellboat: "brønnbåt",
  carrier: "mellomfartøy",
  service: "servicebåt",
};
const OP_LABEL_NO: Record<string, string> = {
  harvest: "slakting",
  delousing: "avlusing",
  maintenance: "vedlikehold",
  feed: "fôr",
  cleaning: "vasking",
  pumping: "pumping",
};

function escHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c,
  );
}

type FindingItem = {
  audit_year: number;
  kind: "avvik" | "forbedringspunkt";
  topic: string;
  summary: string;
  hjemmel?: string | null;
};
type FindingsResp = { count: number; items: FindingItem[] };

// Lazily load an operator's Mattilsynet findings into a popup section.
async function loadAuditFindings(slug: string, el: HTMLElement): Promise<void> {
  try {
    const j = await fetchJSON<FindingsResp>(
      `/v1/compliance/findings?operator_slug=${encodeURIComponent(slug)}&limit=30`,
    );
    const items = j.items ?? [];
    if (items.length === 0) {
      el.innerHTML = "Ingen registrerte avvik.";
      return;
    }
    const nA = items.filter((i) => i.kind === "avvik").length;
    const nF = items.length - nA;
    const head =
      `<div class="audit-counts">` +
      (nA ? `<span class="audit-kind audit-avvik">${nA} avvik</span>` : "") +
      (nF ? `<span class="audit-kind audit-fbp">${nF} forbedringspunkt</span>` : "") +
      `</div>`;
    const list = items
      .map(
        (i) =>
          `<li class="audit-item"><span class="audit-tag audit-${i.kind}">` +
          `${i.kind === "avvik" ? "Avvik" : "Forb."}</span>` +
          `<span class="audit-topic">${escHtml(i.topic)} <span class="audit-yr">${i.audit_year}</span></span>` +
          `<div class="audit-sum">${escHtml(i.summary)}</div></li>`,
      )
      .join("");
    el.classList.remove("muted");
    el.innerHTML = head + `<ul class="audit-list">${list}</ul>`;
  } catch {
    el.innerHTML = "Kunne ikke laste avvik.";
  }
}

async function loadVesselActivity(receiverId: string, el: HTMLElement): Promise<void> {
  try {
    const j = await fetchJSON<VesselActivity>(
      `/v1/receivers/${receiverId}/vessel-activity?days=7`,
    );
    if (!j.n_vessels) {
      el.textContent = "Ingen fartøy registrert nær anlegget";
      return;
    }
    const roleBits = Object.entries(j.by_role)
      .map(([k, n]) => `${n} ${ROLE_LABEL_NO[k] ?? k}`)
      .join(", ");
    const opBits = Object.entries(j.by_op)
      .map(([k, n]) => `${n}× ${OP_LABEL_NO[k] ?? k}`)
      .join(", ");
    let html = `<div class="pa-sum">${j.n_vessels} fartøy · ${roleBits}</div>`;
    if (opBits) html += `<div class="pa-ops">Registrert: ${opBits}</div>`;
    html +=
      `<ul class="pa-list">` +
      j.vessels
        .slice(0, 6)
        .map((v) => {
          const op = v.op_label ? `<span class="pa-op">${escHtml(v.op_label)}</span>` : "";
          return `<li>${escHtml(v.name ?? "ukjent")}${op}</li>`;
        })
        .join("") +
      `</ul>`;
    el.innerHTML = html;
  } catch {
    el.textContent = "kunne ikke laste aktivitet";
  }
}

function makePopupRow(label: string, value: string): HTMLElement {
  const row = document.createElement("div");
  row.className = "popup-row";
  const lab = document.createElement("span");
  lab.className = "popup-label";
  lab.textContent = label;
  row.appendChild(lab);
  const val = document.createElement("span");
  val.className = "popup-value";
  val.textContent = value;
  row.appendChild(val);
  return row;
}

// Decision tool: estimated harvest value if the site slaughters a couple of
// months earlier/later. Biomass grows ~4%/month near harvest (capped at MTB);
// price follows the seasonal forecast; FX from the scenario slider. The
// dominant lever near harvest is the seasonal price, not the small biomass gain.
function buildTimingTool(p: EnrichedProperties): HTMLElement | null {
  if (p.harvest_year == null || p.harvest_month == null || p.harvest_t == null) return null;
  const GROWTH = 0.04;
  const fxf = getFxFactor();
  type Opt = { k: number; y: number; m: number; t: number; price: number; mnok: number };
  const opts: Opt[] = [];
  for (let k = -2; k <= 2; k++) {
    const idx = p.harvest_year * 12 + (p.harvest_month - 1) + k;
    const y = Math.floor(idx / 12);
    const m = (idx % 12) + 1;
    const price = getPrice(y, m);
    if (price == null) continue;
    let t = p.harvest_t * (1 + GROWTH * k);
    if (p.capacity_t != null) t = Math.min(t, p.capacity_t);
    opts.push({ k, y, m, t, price, mnok: (t * price * fxf) / 1000 });
  }
  if (opts.length < 2) return null;
  const planned = opts.find((o) => o.k === 0);
  let best = opts[0]!;
  for (const o of opts) if (o.mnok > best.mnok) best = o;

  const rows = opts.map((o) => {
    const label = `${MONTHS_NO_FULL[o.m - 1]?.slice(0, 3) ?? ""} '${String(o.y).slice(2)}`;
    const d = planned ? Math.round(o.mnok - planned.mnok) : 0;
    const dtxt = o.k === 0 ? "referanse" : `${d >= 0 ? "+" : ""}${d}`;
    const cls = "pt-row" + (o === best ? " is-best" : "") + (o.k === 0 ? " is-planned" : "");
    return `<div class="${cls}"><span>${label}</span>` +
      `<span class="pt-mnok">${Math.round(o.mnok).toLocaleString("no")} MNOK</span>` +
      `<span class="pt-d">${dtxt}</span></div>`;
  }).join("");
  let take = "Anslått slaktemåned er nær optimal";
  if (planned && best !== planned) {
    const d = Math.round(best.mnok - planned.mnok);
    const lbl = MONTHS_NO_FULL[best.m - 1] ?? "";
    take = best.k > 0
      ? `Vent ${best.k} mnd (til ${lbl}) → ~+${d} MNOK`
      : `Slakt ${-best.k} mnd før (${lbl}) → ~+${d} MNOK`;
  }
  const box = document.createElement("div");
  box.className = "popup-timing";
  box.innerHTML =
    `<div class="popup-label">Slaktetiming · verdi etter måned (anslag)</div>` +
    `<div class="pt-rows">${rows}</div>` +
    `<div class="pt-take">${take}</div>`;
  return box;
}

async function downloadWeeklyReport(receiverId: string, btn: HTMLButtonElement): Promise<void> {
  const original = btn.textContent ?? "Last ned ukerapport (PDF)";
  btn.disabled = true;
  btn.textContent = "Genererer …";
  try {
    const url = `${API_BASE}/v1/pdf?type=uro_rapport&receiver_id=${encodeURIComponent(receiverId)}&latest=7d`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    const cd = res.headers.get("content-disposition") ?? "";
    const m = /filename="([^"]+)"/.exec(cd);
    const filename = m?.[1] ?? `uro_rapport_${receiverId}.pdf`;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    btn.textContent = "Lastet ned ✓";
  } catch (err) {
    console.error(err);
    btn.textContent = `Feil: ${(err as Error).message}`;
  } finally {
    setTimeout(() => {
      btn.disabled = false;
      btn.textContent = original;
    }, 2500);
  }
}

// ---------------------------------------------------------------------------
// Filter panel UI · stateless builders, callbacks update filterState
// ---------------------------------------------------------------------------

// The farm-filter controls, as a detachable block embedded in the drawer's
// "Anlegg" section (so a reset can rebuild just this block in place).
function buildFarmFilters(operators: OperatorListItem[], onChange: () => void): HTMLElement {
  const root = document.createElement("div");
  root.className = "farm-filters";

  // Metric mode for the farm layer: biomass bubbles vs noise.
  root.appendChild(makeSegmented(
    "Vis",
    [{ value: "biomass", label: "Biomasse" }, { value: "noise", label: "Støy" }],
    farmMode,
    (v) => setFarmMode(v as FarmMode),
    "seg-farmmode",
  ));

  root.appendChild(makeRadioGroup(
    "Status",
    "filter-status",
    [
      { value: "all", label: "Alle" },
      { value: "active", label: "Aktive" },
      { value: "fallow", label: "Brakklagte" },
    ],
    filterState.status,
    (v) => { filterState.status = v as FilterState["status"]; onChange(); },
  ));

  root.appendChild(makeCheckGroup<Tier>("Uro-tier", TIERS, () => filterState.tiers, (sel) => {
    filterState.tiers = sel;
    onChange();
  }));

  root.appendChild(makeCheckGroup<SpeciesGroup>(
    "Oppdrettstype",
    SPECIES_GROUPS,
    () => filterState.speciesGroups,
    (sel) => { filterState.speciesGroups = sel; onChange(); },
    (g) => SPECIES_GROUP_LABELS[g],
  ));

  root.appendChild(makeOperatorPicker(operators, (slug) => {
    filterState.operatorSlug = slug;
    onChange();
    // Mirror the owner selection onto the vessel layer: show only vessels
    // that have been active at this operator's farms.
    vesselFilter.operatorSlug = slug;
    applyVesselFilter(map, vesselFilter);
  }));

  root.appendChild(makeCheckbox("Bare med lus rapportert", filterState.hasLiceOnly, (v) => {
    filterState.hasLiceOnly = v;
    onChange();
  }));

  root.appendChild(makeCheckbox("Bare med PD eller ILA", filterState.hasViralOnly, (v) => {
    filterState.hasViralOnly = v;
    onChange();
  }));

  const reset = document.createElement("button");
  reset.type = "button";
  reset.className = "filter-reset";
  reset.textContent = "Nullstill anleggsfilter";
  reset.addEventListener("click", () => {
    filterState.status = "all";
    filterState.tiers = defaultTiers();
    filterState.speciesGroups = defaultSpeciesGroups();
    filterState.operatorSlug = "";
    filterState.hasLiceOnly = false;
    filterState.hasViralOnly = false;
    root.replaceWith(buildFarmFilters(operators, onChange));
    onChange();
  });
  root.appendChild(reset);

  const count = document.createElement("div");
  count.className = "filter-count";
  count.id = "filter-count";
  root.appendChild(count);

  return root;
}

// ---------------------------------------------------------------------------
// Drawer · brand header, master layer switches, collapsible sections
// ---------------------------------------------------------------------------

const vesselFilter: VesselFilter = defaultVesselFilter();
// Mirror a restored owner filter onto the vessel layer (the picker's onChange
// normally does this, but a persisted slug bypasses that path).
vesselFilter.operatorSlug = filterState.operatorSlug;

// Farm layer: current metric mode + whether the layer is on (drives the HTML
// cluster bubbles, which aren't real map layers so must be hidden manually).
let farmMode: FarmMode = "biomass"; // dot colour (biomass/noise) when zoomed in
let clusterLens: FarmMode = "biomass"; // what the cluster bubbles encode (board-driven)
let farmVisible = true;
// Set inside the map-load closure (where fullPoints lives) so applyMapLens can
// tag features with has_avvik once compliance audits arrive.
let markAvvik: ((slugs: Set<string>) => void) | null = null;
let farmOpVisible = true; // current-operation rings, on by default
let onshoreVisible = false;
let serviceVisible = false;
let portsVisible = false;

function setFarmMode(mode: FarmMode): void {
  farmMode = mode;
  if (map.getLayer("receivers-circle")) {
    map.setPaintProperty("receivers-circle", "circle-color",
      mode === "biomass" ? colorByBiomass : colorByDb);
    map.setPaintProperty("receivers-circle", "circle-radius",
      mode === "biomass" ? radiusByBiomass : radiusByDb);
  }
  resetClusterMarkers();
  if (farmVisible) updateClusterMarkers(map, "receivers", clusterLens);
  updateFarmLegend(mode);
  // Keep the left-drawer "Vis" toggle in sync when the mode is set
  // programmatically (e.g. by an analytics board lens), not just on click.
  const seg = document.querySelector<HTMLElement>(".seg-farmmode");
  seg?.querySelectorAll<HTMLButtonElement>(".seg-btn").forEach((b) =>
    b.classList.toggle("is-active", b.dataset.val === mode),
  );
}

function updateFarmLegend(mode: FarmMode): void {
  const legend = document.getElementById("legend");
  if (!legend) return;
  const title = legend.querySelector(".legend-title");
  const ramp = legend.querySelector<HTMLElement>(".legend-ramp");
  const scale = legend.querySelector(".legend-scale");
  if (mode === "biomass") {
    if (title) title.textContent = "Anslått biomasse";
    if (ramp) ramp.style.background =
      "linear-gradient(to right,#2a9d8f,#76b17d,#e9c46a,#f4a261,#e76f51)";
    if (scale) scale.innerHTML = "<span>0</span><span>2,5k</span><span>5k+ t</span>";
  } else {
    if (title) title.textContent = "Støynivå (Leq)";
    if (ramp) ramp.style.background =
      "linear-gradient(to right,#2c7bb6,#abd9e9,#ffffbf,#fdae61,#d7191c)";
    if (scale) scale.innerHTML = "<span>115</span><span>135</span><span>155 dB</span>";
  }
}

// Link the active analytics board to what the map shows: the metric you're
// reading on the right is the metric coloured / highlighted on the left.
function setLensVisible(id: string, on: boolean): void {
  if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
}

function applyComplianceLens(): void {
  const apply = (slugs: Set<string>): void => {
    // Zoomed-in: ring the matching sites.
    if (map.getLayer("farm-highlight")) {
      const arr = [...slugs];
      const filter: ExpressionSpecification =
        arr.length > 0
          ? (["all",
              ["!", ["has", "point_count"]],
              ["any", ...arr.map((s) => ["in", s, ["get", "operator_slugs"]])],
            ] as unknown as ExpressionSpecification)
          : (["==", ["get", "kind"], "__none__"] as ExpressionSpecification);
      map.setPaintProperty("farm-highlight", "circle-stroke-color", "#E63926");
      map.setFilter("farm-highlight", filter);
      setLensVisible("farm-highlight", true);
    }
    // Overview: tag features so the cluster avvik_n aggregate fills in.
    markAvvik?.(slugs);
  };
  if (auditSlugs) {
    apply(auditSlugs);
    return;
  }
  const cached = readCache<AuditsResp>("audits");
  if (cached) {
    auditSlugs = slugsFromAudits(cached);
    apply(auditSlugs);
  }
  fetchJSON<AuditsResp>("/v1/compliance/audits?limit=1000")
    .then((r) => {
      writeCache("audits", r);
      auditSlugs = slugsFromAudits(r);
      if (getActiveBoard() === "compliance") apply(auditSlugs);
    })
    .catch((err) => console.error("audits lens", err));
}

function refreshClusters(): void {
  resetClusterMarkers();
  if (farmVisible) updateClusterMarkers(map, "receivers", clusterLens);
}

// Drive a layer card's toggle programmatically — flips the checkbox and fires
// its change handler so the layer + the card UI stay in sync.
function setLayer(toggleId: string, on: boolean): void {
  const input = document.getElementById(toggleId) as HTMLInputElement | null;
  if (!input || input.checked === on) return;
  input.checked = on;
  input.dispatchEvent(new Event("change"));
}

// Focus the map on the active board: turn the distracting layers off so the
// lens stands alone (e.g. avvik shows only flagged farms, nothing else).
function focusLayersForBoard(board: string): void {
  const isVessels = board === "vessels";
  setLayer("layer-vessels", isVessels);
  // Keep farms on even on the vessels board: the fleet reads better against the
  // sites, and the sites must stay clickable when ships are shown.
  setLayer("layer-farms", true);
  setLayer("layer-farmop", board === "ops");
  setLayer("layer-onshore", false);
  setLayer("layer-service", false);
  setLayer("layer-ports", false);
  // Fallback when the drawer toggles don't exist yet (very first paint).
  if (isVessels && !document.getElementById("layer-vessels")) setVesselsVisible(map, true);
}

// Show/hide the gradient ramp (biomass/noise lenses use it; count lenses don't).
function setLegendRamp(on: boolean): void {
  const legend = document.getElementById("legend");
  const ramp = legend?.querySelector<HTMLElement>(".legend-ramp");
  const scale = legend?.querySelector<HTMLElement>(".legend-scale");
  if (ramp) ramp.style.display = on ? "" : "none";
  if (scale) scale.style.display = on ? "" : "none";
}

// A small overlay legend (under the ramp) explaining the active lens.
function setLensLegend(html: string): void {
  const legend = document.getElementById("legend");
  if (!legend) return;
  let el = legend.querySelector<HTMLElement>(".legend-lens");
  if (!html) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("div");
    el.className = "legend-lens";
    legend.appendChild(el);
  }
  el.innerHTML = html;
}

// A selected farm operation → the vessels that perform it. delousing/harvest/
// smolt map to the vessel's live current_op; service boats carry no current_op,
// so they're matched by role instead.
const OP_TO_VESSEL: Record<FarmOp, { cop?: string; role?: string }> = {
  delousing: { cop: "delousing" },
  delousing_due: {}, // a forecast — no vessel is performing it
  harvest: { cop: "harvest" },
  smolt: { cop: "smolt" },
  service: { role: "service" },
};

// Isolate one or more operations on the map (driven by the "Operasjoner nå"
// board, multi-select): the chosen ops' glyphs + rings stay, the other farm dots
// ghost back, and the vessels doing those ops are shown. Empty = restore all.
let opFilter: FarmOp[] = [];
function applyOpFilter(ops: string[]): void {
  const keys = FARM_OP_KEYS as string[];
  opFilter = ops.filter((o): o is FarmOp => keys.includes(o));
  const active = opFilter.length > 0 ? opFilter : keys;
  const opMatch = ["in", ["get", "farm_op"], ["literal", active]] as unknown as ExpressionSpecification;
  const layerFilter = ["all", ["!", ["has", "point_count"]], opMatch] as unknown as ExpressionSpecification;
  if (map.getLayer("farm-op-icon")) map.setFilter("farm-op-icon", layerFilter);
  if (map.getLayer("farm-op-ring")) map.setFilter("farm-op-ring", layerFilter);
  // Ghost the farms that aren't running a selected op (keep coastline context
  // instead of removing them outright).
  if (map.getLayer("receivers-circle")) {
    map.setPaintProperty(
      "receivers-circle",
      "circle-opacity",
      opFilter.length > 0
        ? (["case", ["in", ["get", "farm_op"], ["literal", opFilter]], 0.95, 0.12] as unknown as ExpressionSpecification)
        : 0.95,
    );
  }
  // Also surface the vessels performing the selected ops.
  const overlay =
    opFilter.length > 0
      ? {
          currentOps: opFilter.map((o) => OP_TO_VESSEL[o].cop).filter((v): v is string => !!v),
          roles: opFilter.map((o) => OP_TO_VESSEL[o].role).filter((v): v is string => !!v),
        }
      : null;
  setVesselOpOverlay(map, overlay);
}

function applyMapLens(board: string): void {
  // Focus the map: only the layers relevant to this board stay on.
  focusLayersForBoard(board);
  // The op-isolation filter only makes sense on the ops board; clear it when
  // leaving (the ops board re-asserts its own selection on its next render).
  if (board !== "ops") applyOpFilter([]);
  // Reset the lens-only overlays; each board re-enables what it needs.
  setLensVisible("farm-op-icon", false);
  setLensVisible("farm-highlight", false);
  setLensLegend("");

  const dot = (c: string, label: string): string =>
    `<span class="legend-dot" style="background:${c}"></span>${label}`;
  const ring = (c: string, label: string): string =>
    `<span class="legend-dot" style="background:transparent;border:2px solid ${c}"></span>${label}`;
  const legend = document.getElementById("legend");
  const title = legend?.querySelector<HTMLElement>(".legend-title") ?? null;
  const setTitle = (t: string): void => {
    if (title) title.textContent = t;
  };

  switch (board) {
    case "verdi":
    case "slakt":
      clusterLens = "biomass";
      setLegendRamp(true);
      if (farmVisible) setFarmMode("biomass");
      else { refreshClusters(); updateFarmLegend("biomass"); }
      break;
    case "støy":
      clusterLens = "noise";
      setLegendRamp(true);
      if (farmVisible) setFarmMode("noise");
      else { refreshClusters(); updateFarmLegend("noise"); }
      break;
    case "vessels":
      // Ships board: the fleet is already turned on (and farms off) by
      // focusLayersForBoard; nothing more to draw on the farm layer.
      return;
    case "ops":
      clusterLens = "ops";
      setLegendRamp(false);
      setTitle("Pågående operasjoner");
      setLensVisible("farm-op-icon", true);
      setLensLegend(
        `<div class="legend-lens-row">${dot(FARM_OP_META.delousing.color, "Avlusing")}</div>` +
          `<div class="legend-lens-row">${dot(FARM_OP_META.delousing_due.color, "Avlusing forfaller (lus over grense)")}</div>` +
          `<div class="legend-lens-row">${dot(FARM_OP_META.harvest.color, "Høsting")}</div>` +
          `<div class="legend-lens-row">${dot(FARM_OP_META.smolt.color, "Smolt")}</div>` +
          `<div class="legend-lens-row">${dot(FARM_OP_META.service.color, "Service / fôr")}</div>` +
          `<div class="legend-lens-note">Tydelig symbol = fartøy ved anlegget nå · svakt = nylig (AIS)</div>` +
          `<div class="legend-lens-note">Bobler: antall anlegg i drift</div>`,
      );
      refreshClusters();
      break;
    case "helse":
      clusterLens = "helse";
      setLegendRamp(false);
      setTitle("Lakselus");
      if (map.getLayer("farm-highlight")) {
        map.setPaintProperty("farm-highlight", "circle-stroke-color", "#e76f51");
        map.setFilter("farm-highlight", [
          "all",
          ["!", ["has", "point_count"]],
          ["any",
            [">", ["coalesce", ["get", "avg_lice"], -1], LICE_THRESHOLD],
            ["==", ["get", "has_lice"], true],
          ],
        ] as unknown as ExpressionSpecification);
        setLensVisible("farm-highlight", true);
      }
      setLensLegend(
        `<div class="legend-lens-row">${ring("#e76f51", `Lus over grense (>${LICE_THRESHOLD})`)}</div>` +
          `<div class="legend-lens-note">Bobler: antall anlegg med lus over grense</div>`,
      );
      refreshClusters();
      break;
    case "compliance":
      clusterLens = "compliance";
      setLegendRamp(false);
      setTitle("Mattilsynet-avvik");
      applyComplianceLens();
      setLensLegend(
        `<div class="legend-lens-row">${ring("#E63926", "Anlegg med avvik")}</div>` +
          `<div class="legend-lens-note">Bobler: antall anlegg med avvik</div>`,
      );
      refreshClusters();
      break;
  }
}

function buildDrawer(operators: OperatorListItem[], onFarmChange: () => void): HTMLElement {
  const root = document.createElement("aside");
  root.className = "drawer";
  root.id = "drawer";

  // Mobile grab handle (hidden on desktop) — drag down to dismiss the sheet.
  const handle = document.createElement("div");
  handle.className = "sheet-handle";
  root.appendChild(handle);
  attachSheetDrag(handle, closeSheets);

  // Header: brand mark + collapse toggle.
  const head = document.createElement("div");
  head.className = "drawer-head";
  head.innerHTML =
    `<div class="drawer-brand"><span class="drawer-dot"></span>` +
    `<span><b>Lag</b> · kartlag og filtre</span></div>`;
  const collapse = document.createElement("button");
  collapse.className = "drawer-collapse";
  collapse.type = "button";
  collapse.setAttribute("aria-label", "Skjul panel");
  collapse.textContent = "‹";
  collapse.addEventListener("click", () => {
    root.classList.toggle("is-collapsed");
    collapse.textContent = root.classList.contains("is-collapsed") ? "›" : "‹";
  });
  head.appendChild(collapse);
  root.appendChild(head);

  const body = document.createElement("div");
  body.className = "drawer-body";
  root.appendChild(body);

  // One card per map layer: its on/off, its filters and its legend together.
  body.appendChild(layerCard({
    title: "Anlegg",
    sub: "Støy · helse · lus",
    defaultOn: true,
    toggleId: "layer-farms",
    onToggle: (on) => setFarmVisible(on),
    body: buildFarmFilters(operators, onFarmChange),
  }));
  body.appendChild(layerCard({
    title: "Fartøy",
    sub: "Brønnbåter · servicebåter",
    defaultOn: false, // opt-in over the clean noise map
    toggleId: "layer-vessels",
    onToggle: (on) => setVesselsVisible(map, on),
    body: buildVesselControls(),
  }));
  body.appendChild(layerCard({
    title: "Anlegg – pågående operasjon",
    sub: "Avlusing · høsting · smolt",
    defaultOn: true,
    toggleId: "layer-farmop",
    onToggle: (on) => setFarmOpVisible(on),
    body: buildFarmOpLegend(),
  }));
  body.appendChild(layerCard({
    title: "Landanlegg",
    sub: "Slakteri · mottak · tilvirking",
    defaultOn: false, // opt-in
    toggleId: "layer-onshore",
    onToggle: (on) => {
      onshoreVisible = on;
      setOnshoreVisible(map, on);
      if (on) updateOnshoreClusters(map);
      else resetOnshoreClusters();
    },
    body: buildOnshoreLegend(),
  }));
  body.appendChild(layerCard({
    title: "Servicehavner",
    sub: "Service · bunkring · fôr",
    defaultOn: false, // opt-in
    toggleId: "layer-service",
    onToggle: (on) => {
      serviceVisible = on;
      setServicePortsVisible(map, on);
      if (on) updateServiceClusters(map);
      else resetServiceClusters();
    },
    body: buildServiceLegend(),
  }));
  body.appendChild(layerCard({
    title: "Havner / driftstype",
    sub: "Fôr/service · slakt · fiske",
    defaultOn: false, // opt-in
    toggleId: "layer-ports",
    onToggle: (on) => {
      portsVisible = on;
      setPortsVisible(map, on);
      if (on) updatePortsClusters(map);
      else resetPortsClusters();
    },
    body: buildPortsLegend(),
  }));

  return root;
}

// Legend for the per-farm current-operation rings (farm_op). The ring colour
// matches the vessel current_op halo so a delousing boat at a delousing site
// read as one operation.
function buildFarmOpLegend(): HTMLElement {
  const root = document.createElement("div");
  root.className = "onshore-controls";
  for (const k of FARM_OP_KEYS) {
    const row = document.createElement("div");
    row.className = "legend-row";
    row.innerHTML =
      `<span class="legend-dot" style="background:transparent;` +
      `border:2.5px solid ${FARM_OP_META[k].color}"></span>` +
      `<span>${FARM_OP_META[k].label}</span>`;
    root.appendChild(row);
  }
  const note = document.createElement("div");
  note.className = "filter-note";
  note.textContent =
    "Ring rundt anlegg som har en pågående operasjon nå. Følger anleggslaget.";
  root.appendChild(note);
  return root;
}

// Static legend for the typed-harbour (Havner / driftstype) layer.
function buildPortsLegend(): HTMLElement {
  const root = document.createElement("div");
  root.className = "onshore-controls";
  const items: [string, string][] = [
    [PORTS_META["feed/service"].color, PORTS_META["feed/service"].label],
    [PORTS_META["harvest/slaughter"].color, PORTS_META["harvest/slaughter"].label],
    [PORTS_META.fishing.color, PORTS_META.fishing.label],
  ];
  for (const [color, label] of items) {
    const row = document.createElement("div");
    row.className = "legend-row";
    row.innerHTML =
      `<span class="legend-dot" style="background:${color}"></span>` +
      `<span>${label}</span>`;
    root.appendChild(row);
  }
  const note = document.createElement("div");
  note.className = "filter-note";
  note.textContent =
    "Driftstype klassifisert fra AIS-bevegelser (avganger til anlegg / slakteri). " +
    "Størrelse = antall fartøy; grupperes ved utzooming.";
  root.appendChild(note);
  return root;
}

// Static legend for the (AIS-inferred) service-harbour layer.
function buildServiceLegend(): HTMLElement {
  const root = document.createElement("div");
  root.className = "onshore-controls";
  const row = document.createElement("div");
  row.className = "legend-row";
  row.innerHTML =
    `<span class="legend-dot" style="background:#082e22;border:2px solid ${SERVICE_META.color}"></span>` +
    `<span>Service-, fôr- og liggekaier brukt av flåten</span>`;
  root.appendChild(row);
  const note = document.createElement("div");
  note.className = "filter-note";
  note.textContent =
    "Avledet fra AIS-mønstre — antydende, ikke en offisiell havneliste. " +
    "Bobler grupperer havner ved utzooming; ikonstørrelse = trafikk.";
  root.appendChild(note);
  return root;
}

// Static legend for the onshore-infrastructure layer (no filters yet).
function buildOnshoreLegend(): HTMLElement {
  const root = document.createElement("div");
  root.className = "onshore-controls";
  const items: [string, string][] = [
    [ONSHORE_META.slaughter_farmed.color, "Lakseslakteri (slakting av oppdrettsfisk)"],
    [ONSHORE_META.slaughter_marine.color, "Slakteri, marin fisk"],
    [ONSHORE_META.processing.color, "Tilvirking / mottak"],
  ];
  for (const [color, label] of items) {
    const row = document.createElement("div");
    row.className = "legend-row";
    row.innerHTML =
      `<span class="legend-dot" style="background:${color}"></span>` +
      `<span>${label}</span>`;
    root.appendChild(row);
  }
  const note = document.createElement("div");
  note.className = "filter-note";
  note.textContent =
    "Mattilsynet-godkjente virksomheter. Størrelse = brønnbåttrafikk (siste 14 d); grupperes ved utzooming.";
  root.appendChild(note);
  return root;
}

function setFarmVisible(on: boolean): void {
  farmVisible = on;
  if (map.getLayer("receivers-circle")) {
    map.setLayoutProperty("receivers-circle", "visibility", on ? "visible" : "none");
  }
  // The op rings ride on the farm layer — hide with it, restore to their own
  // toggle state when farms come back.
  if (map.getLayer("farm-op-ring")) {
    map.setLayoutProperty(
      "farm-op-ring", "visibility", on && farmOpVisible ? "visible" : "none",
    );
  }
  if (on) updateClusterMarkers(map, "receivers", clusterLens);
  else resetClusterMarkers();
}

function setFarmOpVisible(on: boolean): void {
  farmOpVisible = on;
  if (map.getLayer("farm-op-ring")) {
    map.setLayoutProperty(
      "farm-op-ring", "visibility", on && farmVisible ? "visible" : "none",
    );
  }
}

// A Carto-style layer card: a compact on/off toggle, a title that expands the
// card, and a body holding that layer's filters + legend.
type CardOpts = {
  title: string;
  sub: string;
  defaultOn: boolean;
  onToggle: (on: boolean) => void;
  body: HTMLElement;
  toggleId?: string;
};

function layerCard(o: CardOpts): HTMLElement {
  const card = document.createElement("section");
  card.className = "lcard" + (o.defaultOn ? "" : " is-off is-closed");

  const header = document.createElement("div");
  header.className = "lcard-head";

  const toggle = document.createElement("label");
  toggle.className = "toggle";
  const input = document.createElement("input");
  input.type = "checkbox";
  if (o.toggleId) input.id = o.toggleId;
  input.checked = o.defaultOn;
  const track = document.createElement("span");
  track.className = "toggle-track";
  track.setAttribute("aria-hidden", "true");
  toggle.appendChild(input);
  toggle.appendChild(track);
  input.addEventListener("change", () => {
    o.onToggle(input.checked);
    card.classList.toggle("is-off", !input.checked);
    card.classList.toggle("is-closed", !input.checked);
  });

  const titleBtn = document.createElement("button");
  titleBtn.type = "button";
  titleBtn.className = "lcard-title";
  titleBtn.innerHTML =
    `<span class="lcard-name">${o.title}</span><span class="lcard-sub">${o.sub}</span>`;
  const toggleOpen = (): void => {
    card.classList.toggle("is-closed");
  };
  titleBtn.addEventListener("click", toggleOpen);

  const chev = document.createElement("button");
  chev.type = "button";
  chev.className = "lcard-chev";
  chev.textContent = "▾";
  chev.addEventListener("click", toggleOpen);

  header.appendChild(toggle);
  header.appendChild(titleBtn);
  header.appendChild(chev);
  card.appendChild(header);

  const bodyWrap = document.createElement("div");
  bodyWrap.className = "lcard-body";
  bodyWrap.appendChild(o.body);
  card.appendChild(bodyWrap);

  return card;
}

function buildVesselControls(): HTMLElement {
  const root = document.createElement("div");
  root.className = "vessel-controls";

  const onChange = (): void => applyVesselFilter(map, vesselFilter);

  // Role toggles, each with its size-band sublabel.
  const roleGroup = document.createElement("div");
  roleGroup.className = "filter-group";
  const legend = document.createElement("div");
  legend.className = "filter-legend";
  legend.textContent = "Fartøystype";
  roleGroup.appendChild(legend);
  for (const r of VESSEL_ROLES) {
    const wrap = document.createElement("label");
    wrap.className = "filter-check vrole";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = vesselFilter.roles.has(r.key);
    input.addEventListener("change", () => {
      if (input.checked) vesselFilter.roles.add(r.key);
      else vesselFilter.roles.delete(r.key);
      onChange();
    });
    wrap.appendChild(input);
    const txt = document.createElement("span");
    txt.innerHTML = `${r.label} <span class="vrole-sub">${r.sub}</span>`;
    wrap.appendChild(txt);
    roleGroup.appendChild(wrap);
  }
  root.appendChild(roleGroup);

  root.appendChild(makeCheckbox("Bare antatt med fisk om bord 🐟", vesselFilter.ladenOnly, (v) => {
    vesselFilter.ladenOnly = v;
    onChange();
  }));

  // Activity legend (what the marker colours mean).
  const legendBox = document.createElement("div");
  legendBox.className = "filter-group";
  const ll = document.createElement("div");
  ll.className = "filter-legend";
  ll.textContent = "Hva fartøyet gjør";
  legendBox.appendChild(ll);
  (Object.keys(STATE_META) as VesselState[]).forEach((s) => {
    const row = document.createElement("div");
    row.className = "vlegend-row";
    row.innerHTML =
      `<span class="vlegend-dot" style="background:${STATE_META[s].color}"></span>` +
      `<span>${STATE_META[s].label}</span>`;
    legendBox.appendChild(row);
  });
  root.appendChild(legendBox);

  // op_role legend (dominant historical role — the corner badge on the marker).
  const opBox = document.createElement("div");
  opBox.className = "filter-group";
  const ol = document.createElement("div");
  ol.className = "filter-legend";
  ol.textContent = "Driftsrolle (merke)";
  opBox.appendChild(ol);
  (Object.keys(OPERATION_META) as VesselOpRole[]).forEach((k) => {
    const row = document.createElement("div");
    row.className = "vlegend-row";
    row.innerHTML =
      `<span class="vlegend-dot" style="background:${OPERATION_META[k].color}"></span>` +
      `<span>${OPERATION_META[k].label}</span>`;
    opBox.appendChild(row);
  });
  root.appendChild(opBox);

  // current_op legend (the LIVE action — the coloured halo/ring on the marker).
  const curBox = document.createElement("div");
  curBox.className = "filter-group";
  const cl = document.createElement("div");
  cl.className = "filter-legend";
  cl.textContent = "Pågående operasjon (ring)";
  curBox.appendChild(cl);
  (Object.keys(CURRENT_OP_META) as CurrentOp[]).forEach((k) => {
    const row = document.createElement("div");
    row.className = "vlegend-row";
    row.innerHTML =
      `<span class="vlegend-dot" style="background:${CURRENT_OP_META[k].color}"></span>` +
      `<span>${CURRENT_OP_META[k].label}</span>`;
    curBox.appendChild(row);
  });
  root.appendChild(curBox);

  const count = document.createElement("div");
  count.className = "filter-count";
  count.id = "vessel-count";
  root.appendChild(count);
  setVesselCountListener((shown, total) => {
    const c = document.getElementById("vessel-count");
    if (c) c.textContent = `${shown} av ${total} fartøy vises`;
    // Keep the Skipstrafikk board live as fleet data arrives / filters change.
    refreshActiveBoard();
  });

  return root;
}

// Segmented (pill) control — one active option at a time.
function makeSegmented(
  label: string,
  options: { value: string; label: string }[],
  current: string,
  onChange: (v: string) => void,
  segClass?: string,
): HTMLElement {
  const group = document.createElement("div");
  group.className = "filter-group";
  const legend = document.createElement("div");
  legend.className = "filter-legend";
  legend.textContent = label;
  group.appendChild(legend);
  const seg = document.createElement("div");
  seg.className = "segmented" + (segClass ? ` ${segClass}` : "");
  const btns: HTMLButtonElement[] = [];
  for (const opt of options) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "seg-btn" + (opt.value === current ? " is-active" : "");
    b.dataset.val = opt.value;
    b.textContent = opt.label;
    b.addEventListener("click", () => {
      btns.forEach((x) => x.classList.remove("is-active"));
      b.classList.add("is-active");
      onChange(opt.value);
    });
    btns.push(b);
    seg.appendChild(b);
  }
  group.appendChild(seg);
  return group;
}

function makeRadioGroup(
  label: string,
  name: string,
  options: { value: string; label: string }[],
  current: string,
  onChange: (v: string) => void,
): HTMLElement {
  const group = document.createElement("div");
  group.className = "filter-group";
  const legend = document.createElement("div");
  legend.className = "filter-legend";
  legend.textContent = label;
  group.appendChild(legend);
  for (const opt of options) {
    const wrap = document.createElement("label");
    wrap.className = "filter-radio";
    const input = document.createElement("input");
    input.type = "radio";
    input.name = name;
    input.value = opt.value;
    input.checked = current === opt.value;
    input.addEventListener("change", () => onChange(opt.value));
    wrap.appendChild(input);
    const span = document.createElement("span");
    span.textContent = opt.label;
    wrap.appendChild(span);
    group.appendChild(wrap);
  }
  return group;
}

function makeCheckGroup<T extends string>(
  label: string,
  options: readonly T[],
  getCurrent: () => Set<T>,
  onChange: (sel: Set<T>) => void,
  labelFor: (v: T) => string = (v) => v,
): HTMLElement {
  const group = document.createElement("div");
  group.className = "filter-group";
  const legend = document.createElement("div");
  legend.className = "filter-legend";
  legend.textContent = label;
  group.appendChild(legend);
  for (const opt of options) {
    const wrap = document.createElement("label");
    wrap.className = "filter-check";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = getCurrent().has(opt);
    input.addEventListener("change", () => {
      // Re-read the live set on each change so toggles compose correctly
      // instead of mutating stale snapshots from registration time.
      const next = new Set(getCurrent());
      if (input.checked) next.add(opt);
      else next.delete(opt);
      onChange(next);
    });
    wrap.appendChild(input);
    const span = document.createElement("span");
    span.textContent = labelFor(opt);
    wrap.appendChild(span);
    group.appendChild(wrap);
  }
  return group;
}

function makeCheckbox(
  label: string,
  current: boolean,
  onChange: (v: boolean) => void,
): HTMLElement {
  const wrap = document.createElement("label");
  wrap.className = "filter-check filter-check-standalone";
  const input = document.createElement("input");
  input.type = "checkbox";
  input.checked = current;
  input.addEventListener("change", () => onChange(input.checked));
  wrap.appendChild(input);
  const span = document.createElement("span");
  span.textContent = label;
  wrap.appendChild(span);
  return wrap;
}

function makeOperatorPicker(
  operators: OperatorListItem[],
  onChange: (slug: string) => void,
): HTMLElement {
  const group = document.createElement("div");
  group.className = "filter-group";
  const legend = document.createElement("div");
  legend.className = "filter-legend";
  legend.textContent = "Eier";
  group.appendChild(legend);

  const labelToSlug = new Map<string, string>(
    operators.map((op) => [titleCaseOperator(op.name), op.slug]),
  );

  const input = document.createElement("input");
  input.type = "text";
  input.className = "filter-input";
  input.placeholder = "Alle eiere · skriv for å søke";
  input.setAttribute("list", "operator-list");
  // Pre-fill from a persisted owner selection so a reload shows it.
  if (filterState.operatorSlug) {
    const restored = operators.find((op) => op.slug === filterState.operatorSlug);
    if (restored) input.value = titleCaseOperator(restored.name);
  }
  group.appendChild(input);

  const datalist = document.createElement("datalist");
  datalist.id = "operator-list";
  for (const op of operators) {
    const o = document.createElement("option");
    o.value = titleCaseOperator(op.name);
    datalist.appendChild(o);
  }
  group.appendChild(datalist);

  const update = (): void => {
    const v = input.value.trim();
    if (!v) { onChange(""); return; }
    onChange(labelToSlug.get(v) ?? "");
  };
  input.addEventListener("change", update);
  input.addEventListener("input", () => {
    if (!input.value.trim()) onChange("");
  });

  return group;
}

// ---------------------------------------------------------------------------
// Board persistence + mobile bottom-sheet behaviour
// ---------------------------------------------------------------------------

const BOARD_KEY = "phoniq-map.board";

function readHashParam(name: string): string | null {
  return new URLSearchParams(location.hash.replace(/^#/, "")).get(name);
}

function initialBoard(): string {
  return readHashParam("board") || localStorage.getItem(BOARD_KEY) || "ops";
}

function persistBoard(id: string): void {
  try {
    localStorage.setItem(BOARD_KEY, id);
  } catch {
    /* ignore quota */
  }
  const params = new URLSearchParams(location.hash.replace(/^#/, ""));
  params.set("board", id);
  history.replaceState(null, "", `#${params.toString()}`);
}

// Mobile: a bottom tab bar toggles the left (Lag) / right (Analyse) panels as
// bottom sheets; a shared backdrop tap and a drag-down on the grab handle close.
function closeSheets(): void {
  document.body.classList.remove("show-drawer", "show-analytics");
  syncSheetUI();
}

function openSheet(which: "drawer" | "analytics"): void {
  const cls = which === "drawer" ? "show-drawer" : "show-analytics";
  const already = document.body.classList.contains(cls);
  document.body.classList.remove("show-drawer", "show-analytics");
  if (!already) document.body.classList.add(cls);
  syncSheetUI();
}

function syncSheetUI(): void {
  const open =
    document.body.classList.contains("show-drawer") ||
    document.body.classList.contains("show-analytics");
  const bd = document.getElementById("sheet-backdrop");
  if (bd) bd.hidden = !open;
  document.querySelectorAll<HTMLElement>("#mobtabs .mt-btn").forEach((b) => {
    const which = b.dataset.sheet;
    b.classList.toggle(
      "is-active",
      (which === "drawer" && document.body.classList.contains("show-drawer")) ||
        (which === "analytics" && document.body.classList.contains("show-analytics")),
    );
  });
}

function attachSheetDrag(handle: HTMLElement, onClose: () => void): void {
  const panel = handle.parentElement;
  let startY = 0;
  let dy = 0;
  let dragging = false;
  handle.addEventListener("pointerdown", (e) => {
    dragging = true;
    startY = e.clientY;
    dy = 0;
    handle.setPointerCapture(e.pointerId);
  });
  handle.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    dy = Math.max(0, e.clientY - startY);
    if (panel) panel.style.transform = `translateY(${dy}px)`;
  });
  const end = (): void => {
    if (!dragging) return;
    dragging = false;
    if (panel) panel.style.transform = "";
    if (dy > 60) onClose();
  };
  handle.addEventListener("pointerup", end);
  handle.addEventListener("pointercancel", end);
}

function setupMobileSheets(): void {
  document.querySelectorAll<HTMLElement>("#mobtabs .mt-btn").forEach((b) => {
    b.addEventListener("click", () => openSheet(b.dataset.sheet as "drawer" | "analytics"));
  });
  document.getElementById("sheet-backdrop")?.addEventListener("click", closeSheets);
  window.addEventListener("hashchange", () => {
    const b = readHashParam("board");
    if (b && b !== getActiveBoard()) setActiveBoard(b);
  });
}

// ---------------------------------------------------------------------------
// Map setup
// ---------------------------------------------------------------------------

// Remember the last camera (centre/zoom/bearing/pitch) across reloads so the
// map reopens exactly where you left it.
const CAMERA_KEY = "phoniq-map.camera";
type SavedCamera = { lng: number; lat: number; zoom: number; bearing: number; pitch: number };

function loadCamera(): SavedCamera | null {
  try {
    const raw = localStorage.getItem(CAMERA_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as SavedCamera;
    if (typeof c.lng === "number" && typeof c.lat === "number" && typeof c.zoom === "number") {
      return c;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function saveCamera(): void {
  try {
    const c = map.getCenter();
    localStorage.setItem(
      CAMERA_KEY,
      JSON.stringify({
        lng: c.lng, lat: c.lat,
        zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch(),
      }),
    );
  } catch {
    /* ignore quota */
  }
}

const savedCamera = loadCamera();

const map = new maplibregl.Map({
  container: "map",
  style: {
    version: 8,
    sources: {
      satellite: {
        type: "raster",
        tiles: [
          `https://api.tiles.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.jpg?access_token=${MAPBOX_TOKEN}`,
        ],
        tileSize: 256,
        attribution:
          '© <a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
      },
    },
    layers: [{ id: "satellite", type: "raster", source: "satellite" }],
  },
  center: savedCamera ? [savedCamera.lng, savedCamera.lat] : [13.5, 65.5],
  zoom: savedCamera ? savedCamera.zoom : 4.2,
  bearing: savedCamera?.bearing ?? 0,
  pitch: savedCamera?.pitch ?? 0,
  attributionControl: { compact: true },
});

// Persist the camera whenever a move settles.
map.on("moveend", saveCamera);

// Bottom-right so they clear the top app bar and the left drawer; CSS shifts
// this group left of the right-hand analytics panel.
map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), "bottom-right");
map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-right");

// Mobile bottom-sheet tab bar (Lag / Analyse) — DOM is already parsed.
setupMobileSheets();

map.on("load", () => {
  const meta = document.getElementById("legend-meta");

  type LoadState = {
    zones: ZonesResponse | undefined;
    heatmap: HeatmapResponse | undefined;
    status: StatusResponse | undefined;
    operators: OperatorsResponse | undefined;
    cycle: CycleResponse | undefined;
    salmon: SalmonResponse | undefined;
    farmOps: FarmOpsResponse | undefined;
  };

  // Hydrate from cache so repeat visits paint instantly while fresh data
  // refreshes in the background. Network calls below will overwrite each.
  const state: LoadState = {
    zones: readCache<ZonesResponse>("zones"),
    heatmap: readCache<HeatmapResponse>("heatmap"),
    status: readCache<StatusResponse>("status"),
    operators: readCache<OperatorsResponse>("operators"),
    cycle: readCache<CycleResponse>("cycle"),
    salmon: readCache<SalmonResponse>("salmon"),
    farmOps: readCache<FarmOpsResponse>("farmOps"),
  };

  let layersBuilt = false;
  let filterMounted = false;
  let analyticsMounted = false;
  let fullPoints: GeoJSON.FeatureCollection<GeoJSON.Point, EnrichedProperties> | null = null;
  let fullZones: GeoJSON.FeatureCollection<GeoJSON.Polygon, EnrichedProperties> | null = null;

  function applyFilters(): void {
    if (!fullPoints || !fullZones) return;
    saveFilters();
    const fp = filterFC(fullPoints);
    const fz = filterFC(fullZones);
    (map.getSource("receivers") as maplibregl.GeoJSONSource | undefined)?.setData(fp);
    (map.getSource("zones") as maplibregl.GeoJSONSource | undefined)?.setData(fz);
    const c = document.getElementById("filter-count");
    if (c) c.textContent = `${fp.features.length} av ${fullPoints.features.length} anlegg vises`;
    // Right-hand analytics reflect the filtered farm set *within the current map
    // viewport* — pan/zoom recomputes them (see the moveend handler below).
    refreshAnalytics(pointsInView());
  }

  // The filtered farm set restricted to the current map bounds. Drives the
  // right-hand analytics so the numbers describe the area you're looking at.
  function pointsInView(): AnalyticsPoint[] {
    if (!fullPoints) return [];
    const b = map.getBounds();
    const out: AnalyticsPoint[] = [];
    for (const f of fullPoints.features) {
      if (!passesFilters(f.properties)) continue;
      const lon = f.geometry.coordinates[0];
      const lat = f.geometry.coordinates[1];
      if (lon == null || lat == null) continue;
      if (b.contains([lon, lat])) out.push(f.properties as AnalyticsPoint);
    }
    return out;
  }

  // Tag features with has_avvik (operator has a Mattilsynet avvik) so the
  // compliance cluster aggregate fills in; then re-push the data.
  markAvvik = (slugs: Set<string>): void => {
    for (const fc of [fullPoints, fullZones]) {
      if (!fc) continue;
      for (const f of fc.features) {
        f.properties.has_avvik = (f.properties.operator_slugs ?? []).some((s) => slugs.has(s));
      }
    }
    applyFilters();
  };

  function buildLayers(): void {
    if (layersBuilt || !fullPoints || !fullZones) return;
    layersBuilt = true;

    map.addSource("zones", { type: "geojson", data: filterFC(fullZones) });
    map.addSource("receivers", {
      type: "geojson",
      data: filterFC(fullPoints),
      cluster: true,
      clusterRadius: 54,
      clusterMaxZoom: 10,
      // Aggregates read by the HTML cluster bubbles, so the overview (clustered)
      // view reflects whichever analytics board is active: Σ biomass, sum+count
      // of dB (mean noise), # sites in operation, # with lice over the limit,
      // and # whose operator has a Mattilsynet avvik.
      clusterProperties: {
        bsum: ["+", ["coalesce", ["get", "est_biomass_t"], 0]],
        dbsum: ["+", ["coalesce", ["get", "db"], 0]],
        dbn: ["+", ["case", ["==", ["typeof", ["get", "db"]], "number"], 1, 0]],
        op_n: ["+", ["case", ["in", ["get", "farm_op"], ["literal", FARM_OP_KEYS as string[]]], 1, 0]],
        lice_n: ["+", ["case",
          ["any",
            [">", ["coalesce", ["get", "avg_lice"], -1], LICE_THRESHOLD],
            ["==", ["get", "has_lice"], true],
          ], 1, 0]],
        avvik_n: ["+", ["case", ["==", ["get", "has_avvik"], true], 1, 0]],
      },
    });

    // Farm zone polygons are noise context — only once zoomed in, so the
    // low-zoom cluster view stays clean.
    // (The translucent 5 km zone buffer fill was removed — it cluttered the
    // overview with overlapping circles. Noise now reads from the dot colour
    // alone, explained in the legend and the site popup.)

    // Individual (unclustered) farms; clusters are drawn as HTML bubbles.
    // Default paint = biomass mode (toggled by setFarmMode).
    map.addLayer({
      id: "receivers-circle",
      type: "circle",
      source: "receivers",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": radiusByBiomass,
        "circle-color": colorByBiomass,
        "circle-stroke-color": "#06231a",
        "circle-stroke-width": 1.2,
        "circle-opacity": 0.95,
      },
    });

    // Farm CURRENT-operation ring: a hollow coloured stroke just outside the
    // farm dot, only on unclustered farms that have a live op (farm_op set).
    // Same op palette as the vessel current_op halo.
    map.addLayer({
      id: "farm-op-ring",
      type: "circle",
      source: "receivers",
      filter: ["all",
        ["!", ["has", "point_count"]],
        ["in", ["get", "farm_op"], ["literal", FARM_OP_KEYS as string[]]],
      ],
      layout: { visibility: farmOpVisible ? "visible" : "none" },
      paint: {
        "circle-radius": farmOpRingRadius,
        "circle-color": "rgba(0,0,0,0)",
        "circle-stroke-color": colorByFarmOp,
        "circle-stroke-width": 2.6,
        "circle-opacity": 1,
      },
    });

    // Lens highlight ring (lice / compliance) — filter + colour set per board
    // by applyMapLens. Hidden until a highlighting board is active.
    map.addLayer({
      id: "farm-highlight",
      type: "circle",
      source: "receivers",
      filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "kind"], "__none__"]],
      layout: { visibility: "none" },
      paint: {
        "circle-radius": farmHighlightRadius,
        "circle-color": "rgba(0,0,0,0)",
        "circle-stroke-color": "#E63926",
        "circle-stroke-width": 3.2,
        "circle-stroke-opacity": 0.95,
      },
    });

    // Per-farm operation glyphs (delousing / harvest / smolt) — shown when the
    // "Operasjoner nå" board is active so ops read as clear icons on the map.
    for (const k of FARM_OP_KEYS) {
      const id = `fop-${k}`;
      if (!map.hasImage(id)) map.addImage(id, farmOpGlyph(k), { pixelRatio: FARM_OP_PR });
    }
    map.addLayer({
      id: "farm-op-icon",
      type: "symbol",
      source: "receivers",
      filter: ["all",
        ["!", ["has", "point_count"]],
        ["in", ["get", "farm_op"], ["literal", FARM_OP_KEYS as string[]]],
      ],
      layout: {
        visibility: "none",
        "icon-image": [
          "match", ["get", "farm_op"],
          "delousing", "fop-delousing",
          "delousing_due", "fop-delousing_due",
          "harvest", "fop-harvest",
          "smolt", "fop-smolt",
          "service", "fop-service",
          "fop-delousing",
        ],
        "icon-size": ["interpolate", ["linear"], ["zoom"], 5, 0.5, 9, 0.74, 13, 1.05],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: {
        // Live (vessel present now) reads full strength; the backend recent-
        // window op is dimmed so it doesn't claim an operation is in progress.
        "icon-opacity": ["case", ["==", ["get", "op_is_live"], true], 1, 0.5],
      },
    });

    // Keep the HTML cluster bubbles in sync with the source on every frame.
    map.on("render", () => {
      if (farmVisible) updateClusterMarkers(map, "receivers", clusterLens);
      if (onshoreVisible) updateOnshoreClusters(map);
      if (serviceVisible) updateServiceClusters(map);
      if (portsVisible) updatePortsClusters(map);
    });

    // Recompute the analytics for the new viewport once a pan/zoom settles.
    map.on("moveend", () => {
      if (fullPoints) refreshAnalytics(pointsInView());
    });
    updateFarmLegend(farmMode);

    const popup = new maplibregl.Popup({ closeButton: true, closeOnClick: true, offset: 10 });

    map.on("click", "receivers-circle", (e) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const raw = feature.properties as Record<string, unknown>;
      const props: EnrichedProperties = {
        ...(raw as unknown as EnrichedProperties),
        operator_slugs: readArrayProp(raw.operator_slugs),
        operator_names: readArrayProp(raw.operator_names),
        species_groups: readArrayProp(raw.species_groups) as SpeciesGroup[],
      };
      popup
        .setLngLat([props.lon, props.lat])
        .setDOMContent(popupContent(props))
        .addTo(map);
      // Mark this site's forecast month on the slaughter chart.
      highlightHarvestMonth(props.harvest_year, props.harvest_month);
    });
    popup.on("close", () => highlightHarvestMonth(null, null));

    map.on("mouseenter", "receivers-circle", () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "receivers-circle", () => {
      map.getCanvas().style.cursor = "";
    });

    // Vessels go on after the farm layers so their pictograms sit on top.
    void initVessels(map).then(() => applyVesselFilter(map, vesselFilter));
    // Onshore infrastructure (slaughter / processing plants) — opt-in layer.
    void initOnshore(map);
    // Service / supply harbours (AIS-inferred) — opt-in layer.
    void initServicePorts(map);
    // Typed harbours by function (improved classification) — opt-in layer.
    void initPorts(map);
  }

  function render(): void {
    if (!state.zones) return;

    cycleByLoc.clear();
    for (const it of state.cycle?.items ?? []) cycleByLoc.set(it.locality_no, it);
    farmOpByLoc.clear();
    for (const it of state.farmOps?.items ?? []) {
      farmOpByLoc.set(it.locality_no, {
        op: it.op,
        live: it.source === "live",
        state: it.state,
        vessel: it.vessel,
        eventDate: it.event_date,
        lastDone: it.last_done,
        reason: it.reason,
        nVessels: it.n_vessels,
        method: it.method,
      });
    }
    if (state.cycle) {
      const asOf = state.cycle.as_of ? new Date(state.cycle.as_of) : new Date();
      const prices = new Map<string, number>(
        state.salmon?.forecast.map((f) => [`${f.year}-${f.month}`, f.price_nok_kg]) ?? [],
      );
      renderHarvestChart(state.cycle.items, asOf, prices);
    }

    const dbByReceiver = new Map<string, number>(
      state.heatmap?.items.map((i) => [i.receiver_id, i.db]) ?? [],
    );
    const statusByReceiver = new Map<string, ReceiverStatus>(
      state.status?.items.map((s) => [s.receiver_id, s]) ?? [],
    );
    const thresholds = state.heatmap
      ? computeThresholds(state.heatmap.items.map((i) => i.db))
      : { p25: NaN, p50: NaN, p75: NaN, p95: NaN };

    fullPoints = buildEnrichedPoints(state.zones, statusByReceiver, dbByReceiver, thresholds);
    fullZones = buildEnrichedZones(state.zones, statusByReceiver, dbByReceiver, thresholds);

    if (!layersBuilt) buildLayers();
    else applyFilters();

    // Mount the right-hand analytics panel once the farm set exists, then
    // populate it from the current filter state.
    if (!analyticsMounted) {
      const anEl = document.getElementById("analytics");
      if (anEl) {
        analyticsMounted = true;
        mountAnalytics(
          anEl,
          {
            fetchJSON,
            readCache,
            writeCache,
            titleCaseOperator,
            farmOpMeta: FARM_OP_META,
            monthsFull: MONTHS_NO_FULL,
            // Summarise only the vessels in the current map view so the board
            // tracks zoom/pan like the farm boards do.
            vesselSummary: () => getVesselSummary(map.getBounds()),
            onOpFilter: applyOpFilter,
          },
          {
            initial: initialBoard(),
            onChange: (id) => {
              persistBoard(id);
              applyMapLens(id);
            },
          },
        );
        const handle = document.createElement("div");
        handle.className = "sheet-handle";
        anEl.prepend(handle);
        attachSheetDrag(handle, closeSheets);
        applyFilters();
        applyMapLens(getActiveBoard());
      }
    }

    if (state.operators && !filterMounted) {
      filterMounted = true;
      document.body.appendChild(buildDrawer(state.operators.items, applyFilters));
      applyFilters();
      // Now that the layer toggles exist, focus them for the active board.
      if (analyticsMounted) applyMapLens(getActiveBoard());
    }

    if (meta) {
      meta.textContent = state.heatmap
        ? `${state.heatmap.items.length} av ${state.zones.count} anlegg · ${formatTime(state.heatmap.from)} → ${formatTime(state.heatmap.to)}`
        : `${state.zones.count} anlegg · lytter etter støynivå …`;
    }

    const fresh = document.getElementById("ab-freshness-text");
    if (fresh) {
      fresh.textContent = state.heatmap
        ? `${state.zones.count.toLocaleString("no")} anlegg · oppdatert ${formatTime(state.heatmap.to)}`
        : "lytter etter data …";
    }

    if (state.heatmap) hideLoading();
  }

  // Paint from cache (instant) before network calls land.
  render();
  if (state.heatmap) hideLoading();
  else if (state.zones) setLoadingText("Lytter etter støynivå …");

  const fetchAndCache = <T>(path: string, key: string, init?: RequestInit): Promise<T> =>
    fetchJSON<T>(path, init).then((v) => {
      writeCache(key, v);
      return v;
    });

  fetchAndCache<ZonesResponse>(
    `/v1/receivers/zones?radius_m=${ZONE_RADIUS_M}`,
    "zones",
  )
    .then((z) => {
      state.zones = z;
      render();
    })
    .catch((err) => {
      console.error("zones", err);
      if (!state.zones && meta) {
        meta.textContent = `Feil ved henting: ${(err as Error).message}`;
      }
      hideLoading();
    });

  fetchAndCache<HeatmapResponse>(
    `/v1/noise/heatmap?latest=${NOISE_WINDOW}&aggregate=${NOISE_AGGREGATE}&limit=20000`,
    "heatmap",
  )
    .then((h) => {
      state.heatmap = h;
      render();
    })
    .catch((err) => {
      console.error("heatmap", err);
      hideLoading();
    });

  fetchAndCache<StatusResponse>("/v1/receivers/status", "status")
    .then((s) => {
      state.status = s;
      render();
    })
    .catch((err) => console.error("status", err));

  fetchAndCache<OperatorsResponse>(
    "/v1/operators?only_with_sites=true&limit=2000",
    "operators",
  )
    .then((o) => {
      state.operators = o;
      render();
    })
    .catch((err) => console.error("operators", err));

  // no-store on the network revalidation so a deploy/restart propagates at
  // once (the instant paint still comes from localStorage above).
  fetchAndCache<CycleResponse>("/v1/farms/cycle", "cycle", { cache: "no-store" })
    .then((cy) => {
      state.cycle = cy;
      render();
    })
    .catch((err) => console.error("cycle", err));

  // 24-month price horizon so even just-stocked cohorts (harvest ~18 mo out)
  // get a forecast price for the value + slaughter-timing tools.
  fetchAndCache<SalmonResponse>("/v1/salmon/price?months_ahead=24", "salmon", { cache: "no-store" })
    .then((sp) => {
      state.salmon = sp;
      render();
    })
    .catch((err) => console.error("salmon", err));

  // Per-farm current operation — no-store so the live state is always fresh.
  fetchAndCache<FarmOpsResponse>("/v1/farms/operations", "farmOps", { cache: "no-store" })
    .then((ops) => {
      state.farmOps = ops;
      render();
    })
    .catch((err) => console.error("farmOps", err));

  // Seed the FX slider with the rate-path-implied NOK move (covered parity).
  fetchJSON<{ implied_nok_pct: number | null; note: string }>("/v1/fx/implied")
    .then((fx) => {
      if (fx.implied_nok_pct != null) setImpliedFx(fx.implied_nok_pct, fx.note);
    })
    .catch((err) => console.error("fx", err));
});
